import { useCallback, useState } from "react";

/** Eén regel voor Google Maps-zoekopdracht (straat, postcode, plaats, land). */
export function formatLocationMapsQuery(c: {
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
}): string | null {
  const parts = [c.address, c.postal_code, c.city, c.country]
    .map((x) => (x ?? "").toString().trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts.join(", ") : null;
}

const iconCopy = (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const iconMap = (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

type CopyProps = {
  text: string;
  /** Korte naam voor aria/title, bijv. "E-mail" of "Groep-e-mail" */
  label: string;
  /** Kleinere knop in compacte lijsten (o.a. groepsleden) */
  compact?: boolean;
};

/** Kopieert platte tekst naar het klembord; korte bevestiging in de titel. */
export function CopyTextButton({ text, label, compact }: CopyProps) {
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      } catch {
        window.alert("Kopiëren lukt niet in deze browser. Selecteer de tekst handmatig.");
      }
    },
    [text]
  );
  const title = copied ? "Gekopieerd!" : `${label} kopiëren`;
  return (
    <button
      type="button"
      className={`detail-action-btn${compact ? " detail-action-btn--compact" : ""}`}
      title={title}
      aria-label={`${label} kopiëren naar klembord`}
      onClick={onClick}
    >
      {iconCopy}
    </button>
  );
}

type MapsProps = {
  query: string;
  compact?: boolean;
};

/** Opent Google Maps in een nieuw tabblad met zoekopdracht `query`. */
export function MapsLinkButton({ query, compact }: MapsProps) {
  const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`detail-action-btn detail-action-link${compact ? " detail-action-btn--compact" : ""}`}
      title="Locatie in Google Maps"
      aria-label="Locatie openen in Google Maps"
      onClick={(e) => e.stopPropagation()}
    >
      {iconMap}
    </a>
  );
}
